import { UpdateTransactionSheet } from "@/components/UpdateTransactionSheet";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import { Checkbox } from "@groot/ui/checkbox";
import { Label } from "@groot/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { useConfirm } from "@groot/ui/primitives";
import {
  useAccounts,
  useBulkDeleteTransactions,
  useCategories,
  useFindDuplicates,
  useMergeTransactions,
  useTags,
} from "@/hooks/api";
import { cn } from "@/lib/utils";
import type { Transaction } from "@/types";
import dayjs from "dayjs";
import {
  AlertCircle,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Combine,
  FileText,
  Hash,
  Loader2,
  User,
} from "lucide-react";
import { useState } from "react";

export function DuplicateTransactions() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [year, setYear] = useState<number>(currentYear);
  const [month, setMonth] = useState<number | undefined>(currentMonth);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [hasSearched, setHasSearched] = useState(false);

  const { data: duplicateGroups = [], isLoading, refetch } = useFindDuplicates(year, month);
  const { data: accounts = [] } = useAccounts();
  const { data: categories = [] } = useCategories();
  const { data: tags = [] } = useTags();
  const bulkDelete = useBulkDeleteTransactions();
  const merge = useMergeTransactions();
  const confirm = useConfirm();

  // Edit transaction state
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);

  const handleSearch = () => {
    setSelectedIds(new Set());
    setHasSearched(true);
    refetch();
  };

  const handleToggle = (id: number) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const handleToggleAll = (group: Transaction[]) => {
    const newSet = new Set(selectedIds);
    const allSelected = group.every((t) => selectedIds.has(t.id));

    if (allSelected) {
      // Deselect all in group
      for (const t of group) {
        newSet.delete(t.id);
      }
    } else {
      // Select all in group
      for (const t of group) {
        newSet.add(t.id);
      }
    }
    setSelectedIds(newSet);
  };

  const handleSelectAllButOldest = (group: Transaction[]) => {
    const newSet = new Set(selectedIds);
    // Sort by createdAt to find the oldest (first created)
    const sorted = [...group].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    // Select all except the oldest one
    for (const t of sorted.slice(1)) {
      newSet.add(t.id);
    }
    // Deselect the oldest one
    newSet.delete(sorted[0].id);

    setSelectedIds(newSet);
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) return;

    if (
      await confirm({
        title: "Confirm Action",
        description: `Are you sure you want to delete ${selectedIds.size} transaction(s)?`,
      })
    ) {
      await bulkDelete.mutateAsync(Array.from(selectedIds));
      setSelectedIds(new Set());
      refetch();
    }
  };

  const handleMerge = async () => {
    if (selectedIds.size < 2) {
      window.alert("Please select at least 2 transactions to merge.");
      return;
    }

    if (
      await confirm({
        title: "Confirm Action",
        description: `Are you sure you want to merge ${selectedIds.size} transaction(s)? This will create a new merged transaction and delete the selected ones. Fold Money metadata will take precedence in case of conflicts.`,
      })
    ) {
      await merge.mutateAsync(Array.from(selectedIds));
      setSelectedIds(new Set());
      refetch();
    }
  };

  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
  const months = [
    { value: undefined, label: "All year" },
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Duplicate Transactions"
        description="Find and remove duplicate transactions from your records"
        className="mb-8"
      />

      <div className="space-y-6">
        {/* Search Criteria */}
        <div className="border border-border rounded-lg p-6">
          <h2 className="text-base font-medium text-foreground mb-1">Search Criteria</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Select a time period to search for duplicates
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
            <div className="flex-1">
              <Label htmlFor="year" className="text-sm">
                Year
              </Label>
              <Select value={year.toString()} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger id="year" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <Label htmlFor="month" className="text-sm">
                Month
              </Label>
              <Select
                value={month?.toString() || "all"}
                onValueChange={(v) => setMonth(v === "all" ? undefined : Number(v))}
              >
                <SelectTrigger id="month" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value || "all"} value={m.value?.toString() || "all"}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleSearch} disabled={isLoading} className="w-full sm:w-auto">
              {isLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  <span className="hidden sm:inline">Searching...</span>
                  <span className="sm:hidden">Search</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Find Duplicates</span>
                  <span className="sm:hidden">Search</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {hasSearched &&
          (duplicateGroups.length === 0 ? (
            <div className="text-center py-20">
              <CheckCircle2 className="w-12 h-12 text-muted-foreground/70 mx-auto mb-4" />
              <h3 className="text-base font-medium text-foreground mb-2">No duplicates found</h3>
              <p className="text-sm text-muted-foreground">
                No duplicate transactions found for the selected period.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-xs md:text-sm text-muted-foreground">
                  Found {duplicateGroups.length} duplicate group(s) with{" "}
                  {duplicateGroups.reduce((sum, group) => sum + group.length, 0)} total transactions
                </p>
                {selectedIds.size > 0 && (
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      onClick={handleMerge}
                      disabled={merge.isPending || selectedIds.size < 2}
                      size="sm"
                      className="flex-1 sm:flex-initial"
                    >
                      {merge.isPending ? (
                        <>
                          <Loader2 className="size-4 animate-spin" />
                          Merging...
                        </>
                      ) : (
                        <>
                          <Combine className="size-4" />
                          <span className="hidden sm:inline">
                            Merge {selectedIds.size} Selected
                          </span>
                          <span className="sm:hidden">Merge ({selectedIds.size})</span>
                        </>
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={bulkDelete.isPending}
                      size="sm"
                      className="flex-1 sm:flex-initial"
                    >
                      {bulkDelete.isPending ? (
                        <>
                          <Loader2 className="size-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <span className="hidden sm:inline">
                            Delete {selectedIds.size} Selected
                          </span>
                          <span className="sm:hidden">Delete ({selectedIds.size})</span>
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-4 md:space-y-6">
                {duplicateGroups.map((group, groupIndex) => (
                  <DuplicateGroup
                    // biome-ignore lint/suspicious/noArrayIndexKey: groups have no unique id
                    key={groupIndex}
                    group={group}
                    groupIndex={groupIndex}
                    selectedIds={selectedIds}
                    onToggle={handleToggle}
                    onToggleAll={handleToggleAll}
                    onSelectAllButOldest={handleSelectAllButOldest}
                    onTransactionClick={(transaction) => {
                      setSelectedTransaction(transaction);
                      setIsEditSheetOpen(true);
                    }}
                  />
                ))}
              </div>
            </>
          ))}
      </div>

      {/* Edit Transaction Sheet */}
      <UpdateTransactionSheet
        transaction={selectedTransaction}
        accounts={accounts}
        categories={categories}
        tags={tags}
        isOpen={isEditSheetOpen}
        onOpenChange={setIsEditSheetOpen}
        onUpdate={() => {
          setSelectedTransaction(null);
          refetch(); // Refresh duplicates after update
        }}
      />
    </Page>
  );
}

interface DuplicateGroupProps {
  group: Transaction[];
  groupIndex: number;
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: (group: Transaction[]) => void;
  onSelectAllButOldest: (group: Transaction[]) => void;
  onTransactionClick: (transaction: Transaction) => void;
}

function DuplicateGroup({
  group,
  groupIndex,
  selectedIds,
  onToggle,
  onToggleAll,
  onSelectAllButOldest,
  onTransactionClick,
}: DuplicateGroupProps) {
  // Use the first transaction as the reference for comparison
  const reference = group[0];

  // Sort group by createdAt (oldest first)
  const sortedGroup = [...group].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const oldestTransaction = sortedGroup[0];
  const allSelected = group.every((t) => selectedIds.has(t.id));

  // Helper to check if a value differs from reference
  // biome-ignore lint/suspicious/noExplicitAny: generic comparison helper
  const isDifferent = (value: any, refValue: any) => {
    if (value === null || value === undefined) return refValue !== null && refValue !== undefined;
    if (refValue === null || refValue === undefined) return true;
    return String(value).toLowerCase().trim() !== String(refValue).toLowerCase().trim();
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="border-b border-border bg-muted px-6 py-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-medium text-foreground flex items-center gap-2">
              <AlertCircle className="size-5 text-warning" />
              Duplicate Group #{groupIndex + 1}
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              {group.length} matching transactions
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleAll(group)}
              className="text-xs"
            >
              {allSelected ? "Deselect All" : "Select All"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSelectAllButOldest(group)}
              className="text-xs"
            >
              Keep Oldest Only
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleAll(group)}
              className="text-xs"
              title="Select all in this group to merge them"
            >
              <Combine className="size-3 mr-1" />
              Select to Merge
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="overflow-x-auto -mx-6 md:mx-0">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden border rounded-lg">
              <Table className="min-w-full">
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="sticky left-0 bg-muted">
                      <span className="sr-only">Select</span>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        <Hash className="size-3" />
                        <span>ID</span>
                      </div>
                    </TableHead>
                    <TableHead className="min-w-[180px]">
                      <div className="flex items-center gap-1">
                        <Building2 className="size-3" />
                        <span>Account</span>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        <Calendar className="size-3" />
                        <span>Date</span>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        <Clock className="size-3" />
                        <span>Bank Time</span>
                      </div>
                    </TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead className="min-w-[200px]">
                      <div className="flex items-center gap-1">
                        <FileText className="size-3" />
                        <span>Description / Merchant</span>
                      </div>
                    </TableHead>
                    <TableHead>Ref No</TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        <User className="size-3" />
                        <span>Source</span>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        <Clock className="size-3" />
                        <span>Created</span>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedGroup.map((transaction) => {
                    const isOldest = transaction.id === oldestTransaction.id;
                    const isSelected = selectedIds.has(transaction.id);

                    return (
                      <TableRow
                        key={transaction.id}
                        className={cn(
                          isSelected && "bg-destructive/10",
                          isOldest && "bg-success/10",
                        )}
                      >
                        <TableCell className="sticky left-0 bg-inherit">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`txn-${transaction.id}`}
                              checked={isSelected}
                              onCheckedChange={() => onToggle(transaction.id)}
                            />
                            {isOldest && (
                              <Badge
                                variant="outline"
                                className="text-[10px] bg-success/10 border-success/30 text-success px-1 py-0"
                              >
                                Oldest
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="font-mono text-xs cursor-pointer hover:bg-muted"
                            onClick={() => onTransactionClick(transaction)}
                          >
                            {transaction.id}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {transaction.account && (
                            <div className="flex items-center gap-2">
                              {transaction.account.iconUrl && (
                                <img
                                  src={transaction.account.iconUrl}
                                  alt={transaction.account.accountName}
                                  className="size-4 rounded flex-shrink-0"
                                />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium truncate">
                                  {transaction.account.accountName}
                                </p>
                                {transaction.account.lastFourAccountNumber && (
                                  <p className="text-[10px] text-muted-foreground font-mono">
                                    ••
                                    {transaction.account.lastFourAccountNumber}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "px-3 py-3 text-xs whitespace-nowrap",
                            isDifferent(transaction.date, reference.date) && "bg-warning/10",
                          )}
                        >
                          {dayjs(transaction.date).format("MMM DD, YYYY HH:mm")}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {(() => {
                            // biome-ignore lint/suspicious/noExplicitAny: meta access
                            const meta = transaction.meta as any;
                            if (
                              meta?.bankSyncProvider === "fold_money" &&
                              meta?.foldMoneyMeta?.txnTimestamp
                            ) {
                              return (
                                <span className="text-warning font-medium">
                                  {meta.foldMoneyMeta.txnTimestamp}
                                </span>
                              );
                            }
                            return <span className="text-muted-foreground/70">-</span>;
                          })()}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "px-3 py-3 text-xs font-medium whitespace-nowrap",
                            transaction.type === "credit" ? "text-success" : "text-destructive",
                            isDifferent(transaction.amount, reference.amount) && "bg-warning/10",
                          )}
                        >
                          {transaction.type === "credit" ? "+" : "-"}₹
                          {Math.abs(transaction.amount).toFixed(2)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "px-3 py-3 text-xs",
                            isDifferent(
                              transaction.description || transaction.merchantName,
                              reference.description || reference.merchantName,
                            ) && "bg-warning/10",
                          )}
                        >
                          <div className="max-w-[250px]">
                            <p className="truncate font-medium">
                              {transaction.merchantName ||
                                transaction.description ||
                                transaction.payee ||
                                "-"}
                            </p>
                            {transaction.merchantName && transaction.description && (
                              <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                                {transaction.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell
                          className={cn(
                            "px-3 py-3 text-xs font-mono",
                            isDifferent(transaction.referenceNo, reference.referenceNo) &&
                              "bg-warning/10",
                          )}
                        >
                          {transaction.referenceNo || "-"}
                        </TableCell>
                        <TableCell
                          className={cn(
                            isDifferent(transaction.addedBy, reference.addedBy) && "bg-warning/10",
                          )}
                        >
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {transaction.addedBy.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          {dayjs(transaction.createdAt).format("MMM DD HH:mm")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        <div className="mt-3 p-3 bg-muted rounded-md text-xs text-muted-foreground">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-warning/10 border border-warning/30 rounded" />
              <span>Different from first transaction</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-success/10 border border-success/30 rounded" />
              <span>Oldest transaction (created first)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-destructive/10 border border-destructive/30 rounded" />
              <span>Selected for deletion</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
